import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, Send, Heart } from "lucide-react";
import { api } from "@/lib/data";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success("Thank you — your message has reached me. I read every one.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="md:col-span-5"
        >
          <p className="overline text-terracotta">Say Hello</p>
          <h1 className="mt-3 font-heading text-5xl sm:text-6xl tracking-tight leading-none text-ink">
            Let's <span className="italic text-terracotta">talk</span>.
          </h1>
          <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-md">
            Whether you'd like to share a story, request prayer, invite me to speak, or
            simply introduce yourself — I'd love to hear from you.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sand-alt text-terracotta">
                <Mail size={20} />
              </span>
              <div>
                <p className="overline text-ink-soft">Email</p>
                <p className="font-body text-ink">chiefapostlelucasjr@gmail.com</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sand-alt text-terracotta">
                <MapPin size={20} />
              </span>
              <div>
                <p className="overline text-ink-soft">Based in</p>
                <p className="font-body text-ink">New Orleans, Louisiana</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sand-alt text-terracotta">
                <Heart size={20} />
              </span>
              <div>
                <p className="overline text-ink-soft">Prayer Requests</p>
                <p className="font-body text-ink">Always confidential, always welcome</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          className="md:col-span-7 rounded-3xl bg-white border border-black/5 shadow-xl p-8 md:p-10"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="overline text-ink-soft">Name</label>
              <input
                required
                value={form.name}
                onChange={update("name")}
                data-testid="contact-name"
                className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
            </div>
            <div>
              <label className="overline text-ink-soft">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                data-testid="contact-email"
                className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="overline text-ink-soft">Subject</label>
            <input
              value={form.subject}
              onChange={update("subject")}
              data-testid="contact-subject"
              className="mt-2 w-full rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>
          <div className="mt-5">
            <label className="overline text-ink-soft">Message</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              className="mt-2 w-full resize-none rounded-2xl border border-black/10 bg-sand/40 px-4 py-3.5 font-body focus:outline-none focus:ring-2 focus:ring-terracotta"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 font-medium text-white transition-all hover:bg-terracotta-hover hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-forest"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <><Send size={18} /> Send Message</>}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
